(() => {
  if (typeof window.customElements.get('location-section') !== 'undefined') { return }
  window.customElements.define('location-section', class LocationsSection extends HTMLElement { // eslint-disable-line
    constructor() {
      super()
      this.mapEl = this.querySelector('[data-location-map]')
      this.mapWrapper = this.querySelector('[data-location-map-wrapper]')
      this.locationMetaEl = this.querySelector('[data-location-meta]')
      this.googleEl = this.querySelector('[data-location-google]')
      this.googleRatingEl = this.querySelector('[data-google-rating]')
      this.googleReviewButton = this.querySelector('[data-leave-review]')
      this.googleStars = this.querySelectorAll('[data-google-star]')
      this.aboutEl = this.querySelector('[data-location-about]')
      this.optometristsGrid = this.querySelector('[data-opto-grid]')
      this.urlDefaultUserIconVisique = 'https://cdn.shopify.com/s/files/1/0550/4820/8622/files/default_user_icon_cb9d20a9-1b47-44ec-8e53-d73d3d48cc8c.png?v=1707432897'
      this.urlDefaultUserIconFYi = 'https://cdn.shopify.com/s/files/1/0257/6652/7012/files/default_user_icon_0dcba17b-15ce-497c-a0bb-39dfc0ac98fa.png?v=1707431757'
      // Visique clinic externalIds that should NOT show a booking button (e.g., merged/closed clinics).
      // Add more IDs to this array to hide additional Visique locations from booking.
      this.visiqueHiddenBookingIds = ['508']
      this.token = localStorage.getItem('LRTokenKey');
      this.locationId = this.dataset.locationId
      this.locale = window.locations.locale // Remove if unused
      this.locationData = {}
      this.doctors = []
      this.attempts = 0
      this.map = null
      this.getLocationData()
    }

    async getLocationData() {
      // The core location record drives the entire section — without it initContent() can't run
      // (it reads consortium, name, address, hours, etc., and only then reveals the hidden-on-
      // page-load content). The upstream API is prone to cold-start timeouts, so fetch it with a
      // timeout + one retry; if it still fails, show a contained error state instead of letting
      // the section collapse to a blank, zero-height area.
      const loaded = await this.fetchLocationData()
      if (!loaded) {
        this.handleLoadError()
        return
      }

      // Per-doctor fetches are best-effort: a failure just drops that doctor's card and must
      // never block initContent(). Default to [] so Promise.allSettled always gets an iterable —
      // previously `undefined` here threw "not iterable" and aborted the whole render.
      const doctorList = this.locationData.doctors || []
      const promises = doctorList.map(async (doctor) => {
        // Use Shopify's real per-request locale (seeded into window.locations.language
        // from {{ request.locale.iso_code }}) rather than sniffing the URL. Correct on
        // both stores: FYidoctors is always 'en'; Visique is 'fr' at the root and 'en'
        // under /en (which the old href.includes('fr') check never detected).
        const language = (window.locations && window.locations.language || '').toLowerCase().startsWith('fr') ? 'fr' : 'en'
        const url = `${window.locations.api_url}/doctors/${doctor.externalId.replace(/E$/, '')}?lang=${language}`
        await fetch(url).then(resp => {
          if (resp.ok) return resp.json()
          throw new Error(resp.statusText)
        })
          .then((resp) => {
            this.doctors.push(resp)
            return resp
          })
          .catch((error) => {
            console.log(error)
          });
      })
      await Promise.allSettled(promises)
      this.initContent()
    }

    // Fetches the core location record with an 8s timeout per attempt and one retry, to ride out
    // the API's cold-start slowness (a first request that times out often succeeds on the next).
    // Returns true on success (this.locationData populated), false if every attempt failed.
    async fetchLocationData(retries = 1) {
      for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 8000)
        try {
          const response = await fetch(`${window.locations.api_url}/locations/v3/${this.locationId}`, { signal: controller.signal })
          if (response.status !== 200) throw new Error('Request failed with status: ' + response.status)
          this.locationData = await response.json()
          return true
        } catch (error) {
          console.error(`Location data fetch failed (attempt ${attempt + 1} of ${retries + 1}):`, error)
        } finally {
          clearTimeout(timeoutId)
        }
      }
      return false
    }

    // Reveals the server-rendered error block (sections/location.liquid, [data-location-error])
    // and wires its "Try again" button to re-run the load. Shown when the location API can't be
    // reached, so the user gets a clear message + recovery path instead of a blank section.
    handleLoadError() {
      const errorEl = this.querySelector('[data-location-error]')
      if (!errorEl) { return }
      errorEl.style.display = ''

      const retryButton = errorEl.querySelector('[data-location-retry]')
      if (retryButton && !retryButton.dataset.bound) {
        retryButton.dataset.bound = 'true'
        retryButton.addEventListener('click', () => {
          errorEl.style.display = 'none'
          this.doctors = []
          this.getLocationData()
        })
      }
    }

    // Returns { hidden, callToBook, url } describing the booking link state for this location.
    // Rules:
    //   - Visique + externalId in visiqueHiddenBookingIds  -> hidden (merged/closed clinics)
    //   - FYidoctors + webSchedulerEnabled=false           -> "Call to Book" non-clickable text
    //   - Otherwise                                        -> normal booking link with appropriate URL
    getBookingLinkConfig() {
      const isFyi = this.locationData.consortium.toUpperCase() == 'FYIDOCTORS'
      const enabled = this.locationData.webSchedulerEnabled
      const clinicId = String(this.locationData.externalId)

      if (!isFyi && this.visiqueHiddenBookingIds.includes(clinicId)) return { hidden: true }
      if (isFyi && !enabled) return { hidden: false, callToBook: true, phoneNumber: this.locationData.phoneNumber }

      const url = isFyi
        ? 'https://fyidoctors.com/pages/book-an-appointment?locationId='
        : enabled
          ? 'https://visique.com/pages/rendez-vous?locationId='
          : 'https://visique.com/pages/demander-un-rendez-vous?locationId='

      return { hidden: false, url }
    }

    async initContent() {
      // Sets clinic name heading
      this.locationMetaEl.getElementsByClassName('location__title')[0].textContent = this.locationData.name
      // Sets booking btn
      const linkButton = this.locationMetaEl.getElementsByClassName("location__actions")[0].getElementsByTagName("a")[0]
      if (linkButton) {
        const config = this.getBookingLinkConfig()
        if (config.hidden) {
          linkButton.classList.add('hidden')
        } else if (config.callToBook) {
          const phone = config.phoneNumber ? config.phoneNumber.replace(/\D/g, '') : ''
          if (phone) {
            linkButton.setAttribute('href', `tel:${phone}`)
          } else {
            linkButton.removeAttribute('href')
          }
          linkButton.textContent = 'Call to Book'
        } else {
          linkButton.setAttribute('href', `${config.url}${this.locationData.externalId}`)
          linkButton.textContent = window.locations.text.book_appointment
        }
      }

      // Sets directions btn
      const addressButton = this.locationMetaEl.getElementsByClassName("location__actions")[0].getElementsByTagName("a")[1]
      addressButton.setAttribute('href', `https://www.google.com/maps/dir/?api=1&destination=${this.locationData.latitude},${this.locationData.longitude}&travelmode=driving&dir_action=navigate`)
      addressButton.textContent = window.locations.text.directions
      // Sets phone link
      const phoneLink = this.locationMetaEl.getElementsByClassName("location__meta")[0].getElementsByClassName("location__phone")[0].getElementsByTagName("a")[0]
      if (this.locationData.phoneNumber && phoneLink) {
        phoneLink.setAttribute('data-id', this.locationData.phoneNumber)
        phoneLink.setAttribute('href', `tel:${this.locationData.phoneNumber}`)
        phoneLink.textContent = `${formatPhoneNumber(this.locationData.phoneNumber)}`
      } else {
        phoneLink.classList.add("hidden")
      }
      // Sets email link
      const emailLink = this.locationMetaEl.getElementsByClassName("location__meta")[0].getElementsByClassName("location__email")[0].getElementsByTagName("a")[0]
      if (this.locationData.email && emailLink) {
        emailLink.setAttribute('href', `mailto:${this.locationData.email}`)
        emailLink.textContent = this.locationData.email
      } else {
        emailLink.classList.add("hidden")
      }
      // Sets address panel
      const addressPanel = this.locationMetaEl.getElementsByClassName("location__meta")[0].getElementsByClassName("location__address")[0]
      addressPanel.getElementsByClassName("location__street-address")[0].textContent = `${this.locationData.addressLine1},`
      addressPanel.getElementsByClassName("location__city")[0].textContent = `${this.locationData.city},`
      addressPanel.getElementsByClassName("location__province")[0].textContent = `${this.locationData.state}`
      // Sets specialties
      if (this.locationData.offers) {
        const specHeading = this.locationMetaEl.getElementsByClassName("location__spec")[0].getElementsByTagName("h2")[0]
        const locationBadges = this.locationMetaEl.getElementsByClassName("location__spec")[0].getElementsByClassName("location__badges")[0]
        const data = this.locationData.offers[0].split('|')
        const badges = data.reduce((acc, currVal) => acc + `<div class="location__badge">${currVal.trim()}</div>`, '')
        specHeading.textContent = `${data.length > 1 ? `${window.locations.locale == 'en' ? 'Specialties' : 'Spécialités'}` : `${window.locations.locale == 'en' ? 'Speciality' : 'Spécialité'}`}`
        locationBadges.textContent = badges
        this.locationMetaEl.getElementsByClassName("location__spec")[0].classList.remove('visually-hidden')
      }
      // Set hours
      const tableRows = this.locationData.openHours?.map((hour, index) => {
        const openParseTime = new Date(`1970/01/01 ${hour.openTime}`);
        const closeParseTime = new Date(`1970/01/01 ${hour.closeTime}`);
        let formattedOpenTime = openParseTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        formattedOpenTime = formattedOpenTime.charAt(0) === '0' ? formattedOpenTime.substring(1) : formattedOpenTime
        let formattedCloseTime = closeParseTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        formattedCloseTime = formattedCloseTime.charAt(0) === '0' ? formattedCloseTime.substring(1) : formattedCloseTime
        return `
          <tr>
            <td class="location__hours-day">${window.locations.text.days[index]}</td>
            <td class="location__hours-open">${hour.openTime !== null ? formattedOpenTime : window.locations.text.closed}</td>
            <td class="location__hours-close">${hour.closeTime !== null ? formattedCloseTime : `<span class="visually-hidden">${window.locations.text.closed}</span>`}</td>
          </tr>
        `
      })
      const note = this.locationData.openHoursNote ? `<span class="location__hours-note">${this.locationData.openHoursNote.replace('|', '<br/>')}</span>` : ''
      this.locationMetaEl.getElementsByClassName("location__hours")[0].getElementsByTagName("h2")[0].textContent = window.locations.text.hours
      const hoursTable = this.locationMetaEl.getElementsByClassName("location__hours")[0].getElementsByTagName("table")[0]
      hoursTable.classList.add(`location__hours-table--${window.locations.language}`)
      hoursTable.insertAdjacentHTML('beforeEnd', tableRows?.join(''))
      this.locationMetaEl.getElementsByClassName("location__hours")[0].insertAdjacentHTML('beforeEnd', note)
      // Sets the about info (language resolved by localizedDescription()).
      this.aboutEl.textContent = this.localizedDescription() || ''
      await this.loadOptometrists()
      await this.initMap()
      await this.checkToken()
      await this.initGoogleReviewsWidget()
      this.initExpandingContent()
      this.updateSchema()
      //  Shows elements
      const elements = document.getElementsByClassName('hidden-on-page-load')
      Array.from(elements).forEach(element => {
        element.classList.remove('visually-hidden');
      });
    }

    // Returns the clinic description in the current page's language.
    // Language comes from window.locations.language, which Liquid seeds from
    // {{ request.locale.iso_code }} — Shopify's real per-request locale. This works
    // unchanged on both stores: FYidoctors is always 'en'; Visique is 'fr' at the root
    // and 'en' under /en. No URL sniffing or per-store branching.
    // Falls back to the other language if the preferred field is empty, since some
    // clinics only have one description populated (e.g. French copy in descriptionEn
    // with descriptionFr blank) — the About must never render blank.
    localizedDescription() {
      const isFrench = (window.locations && window.locations.language || '').toLowerCase().startsWith('fr')
      return isFrench
        ? (this.locationData.descriptionFr || this.locationData.descriptionEn)
        : (this.locationData.descriptionEn || this.locationData.descriptionFr)
    }

    // Enriches the server-side schema.org tag (id="clinic-schema") with dynamic API data.
    // Liquid seeds the tag with name, url, and description (from metafield if set).
    // This method merges in telephone, email, address, geo, and opening hours once
    // locationData is available. Closed days (null openTime/closeTime) are excluded.
    // Description: metafield value takes priority; API description is used as fallback.
    updateSchema() {
      const schemaEl = document.getElementById('clinic-schema')
      if (!schemaEl) return

      const existing = JSON.parse(schemaEl.textContent)
      const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

      // Filter out closed days — schema.org openingHoursSpecification only lists open days
      const openingHours = this.locationData.openHours
        ?.filter(h => h.openTime && h.closeTime)
        .map((h, i) => ({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: `https://schema.org/${days[i]}`,
          opens: h.openTime,
          closes: h.closeTime
        })) || []

      // Language resolved by localizedDescription() (same logic as the About section).
      const apiDescription = this.localizedDescription()

      const schema = {
        ...existing,
        ...(this.locationData.phoneNumber && { telephone: this.locationData.phoneNumber }),
        ...(this.locationData.email && { email: this.locationData.email }),
        // Only use API description if metafield didn't already set one in Liquid
        ...(!existing.description && apiDescription && { description: apiDescription }),
        address: {
          '@type': 'PostalAddress',
          streetAddress: this.locationData.addressLine1,
          addressLocality: this.locationData.city,
          addressRegion: this.locationData.state,
          postalCode: this.locationData.zip,
          addressCountry: 'CA'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: this.locationData.latitude,
          longitude: this.locationData.longitude
        },
        ...(openingHours.length > 0 && { openingHoursSpecification: openingHours })
      }

      schemaEl.textContent = JSON.stringify(schema)
    }

    initExpandingContent() {
      const expandingContentEls = this.querySelectorAll('[data-expanding-content]')
      expandingContentEls.forEach(expandingContentEl => {
        const readMoreButton = expandingContentEl.querySelector('[data-expand-content]')
        readMoreButton.addEventListener('click', () => {
          expandingContentEl.classList.toggle('expanded')
          readMoreButton.textContent = expandingContentEl.classList.contains('expanded') ? readMoreButton.dataset.lessText : readMoreButton.dataset.moreText
        })
      });
    }

    async loadOptometrists() {
      const gridItems = this.doctors.map((doctor) => {
        if (doctor.image === 'https://5154860-sb1.app.netsuite.com/' || !doctor.image || doctor.image === 'null') {
          if (window.location.href.includes('visique')) {
            doctor.image = this.urlDefaultUserIconVisique
          } else {
            doctor.image = this.urlDefaultUserIconFYi
          }
        }
        return `
          <div class="location__optometrists-grid-item">
            <div class="location__optometrist">
              <div class="location__optometrist-header">
                <img src="${doctor.image}" alt="${doctor.name}" width="70" height="70" class="location__optometrist-image">
                <h3 class="location__optometrist-name">
                  ${doctor.name}
                </h3>
              </div>
              ${this.renderDoctorContent(doctor)}
            </div>
          </div>
        `
      })
      gridItems.push(this.renderBookAptCta())
      this.optometristsGrid.innerHTML = gridItems.join('')
    }

    renderBookAptCta() {
      const config = this.getBookingLinkConfig()
      if (config.hidden) { return '' }
      const phone = config.phoneNumber ? config.phoneNumber.replace(/\D/g, '') : ''
      const ctaEl = config.callToBook
        ? (phone
            ? `<a href="tel:${phone}" class="btn">Call to Book</a>`
            : `<a class="btn">Call to Book</a>`)
        : `<a href="${config.url}${this.locationData.externalId}" class="btn">
              ${window.locations.text.book_appointment_cta}
            </a>`
      return `
        <div class="location__optometrists-grid-item location__optometrists-grid-item--cta">
          <div class="location__optometrist-cta-card">
            <h3 class="location__optometrist-cta-title">
              ${window.locations.text.book_appointment_copy}
            </h3>
            ${ctaEl}
          </div>
        </div>
      `
    }

    renderDoctorContent(doctor) {
      if (doctor.bio === 'N/A' || doctor.bio === 'null' || doctor.bio === null || doctor.bio === 'Empty') return ''
      return `
        <div class="location__optometrist-content-wrapper" data-expanding-content>
          <p class="location__optometrist-content" >
            ${doctor.bio}
          </p>
          <button
            class="txt-link"
            data-expand-content
            data-more-text="${window.locations.text.read_more}"
            data-less-text="${window.locations.text.read_less}"
          >
            ${window.locations.text.read_more}
          </button>
        </div>
      `
    }

    renderPrefEl(id) {
      if (!window.locations.is_customer) { return }

      const prefEl = this.querySelector('[data-location-pref]')
      if (id === this.locationData.externalId) {
        prefEl.innerHTML = `
          <div class="location__pref-clinic">
            <span class="location__pref-clinic-label">${window.locations.text.preferred_clinic}</span>
          </div>
        `
      } else {
        prefEl.innerHTML = `
          <location-pref-button class="location__make-preferred-wrapper" data-location-id="${this.locationData.externalId}">
            <button
              type="button"
              class="location__make-preferred"
              data-prefer-this-location
            >
              ${window.locations.text.make_preferred_clinic}
            </button>
          </location-pref-button>
        `
      }
    }



    checkToken() {
      if (!window.locations.is_customer) { return }

      if (this.token) {
        this.fetchPreferedClinic(this.token)
      } else if (!this.token && this.attempts < 10) {
        this.attempts++
        setTimeout(this.checkToken, 300)
      }
    }

    async fetchPreferedClinic(tokenString) {
      if (!localStorage.getItem('pc') || localStorage.getItem('pc') == 'undefined') {
        await fetch(`${window.locations.api_url}/customer/preferred-clinic`, {
          method: 'GET',
          headers: {
            'Authorization': 'Bearer ' + tokenString,
          }
        })
          .then(response => {
            if (response.status === 200) {
              return response.json();
            } else {
              return null;
            }
          })
          .then(data => {
            if (!data) { return }
            this.renderPrefEl(data.locationId)
          })
          .catch(error => {
            console.error(error);
          });
      } else {
        this.renderPrefEl(Number(localStorage.getItem('pc')))
      }
    }

    async updatePreferredClinic() {
      const body = JSON.stringify({ 'locationId': this.locationId })
      await fetch(`${window.locations.api_url}/customer/preferred-clinic`, {
        method: 'POST',
        headers: {
          'Authorization': 'Bearer ' + this.token,
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: body
      })
        .then((response) => {
          if (response.status === 200) {
            return response.json();
          } else {
            throw new Error('Request failed with status: ' + response.status);
          }
        })
        .then((result) => {
          localStorage.setItem('pc', result.locationId);
          this.renderPrefEl(result.locationId)
        })
        .catch((error) => {
          console.error(error)
        });
    }

    // Uses google.maps.places.PlacesService (deprecated March 2025 for new customers).
    // Migration to google.maps.places.Place requires the Maps JS loader in theme.liquid
    // to be updated to use importLibrary() or a newer API version — deferred until
    // theme.liquid is accessible. Existing customers retain access with 12 months notice
    // before discontinuation.
    // Also pending: google.maps.Marker migration to AdvancedMarkerElement (requires mapId).
    async initGoogleReviewsWidget() {
      const service = new google.maps.places.PlacesService(this.map)
      service.getDetails({ placeId: window.locations.place_id }, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK) { return }

        const roundedAvg = Math.ceil(place.rating)
        this.googleEl.classList.remove('hide')
        this.googleRatingEl.textContent = place.rating
        this.googleReviewButton.setAttribute('href', `https://search.google.com/local/writereview?placeid=${place.place_id}`)
        this.googleStars.forEach((star, index) => {
          star.classList.toggle('hide', index + 1 > roundedAvg)
        })
      })
    }

    async initMap() {
      const lat = window.innerWidth < 1024 ? (this.locationData.latitude - 0.007) : this.locationData.latitude
      const lng = window.innerWidth < 1024 ? this.locationData.longitude : (this.locationData.longitude + 0.007)
      this.map = new google.maps.Map(this.mapEl, {
        center: { lat: lat, lng: lng },
        zoom: (window.innerWidth < 1024 ? 14 : 15.5),
        gestureHandling: 'none', // Disable Zoom/Scroll
        scaleControl: false,
        scrollwheel: false,
        disableDefaultUI: true,

        mapTypeControl: false, // Hide the Satellite view toggle
        streetViewControl: false, // Hide the Street View option
        fullscreenControl: false, // Hide the Fullscreen option
      });

      const hideLabels = this.dataset.hideLabels === 'true'
      try {
        const res = await fetch(window.locations.map_json_url)
        if (!res.ok) { throw new Error(res.statusText) }

        const data = await res.json()
        if (hideLabels) {
          data.push(
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{
                visibility: 'off'
              }]
            }
          )
        }
        this.map.setOptions({ styles: data })
      } catch (err) {
        console.error(err)
      }


      this.marker = new google.maps.Marker({
        position: {
          lat: this.locationData.latitude,
          lng: this.locationData.longitude,
        },
        icon: window.locations.marker_svg_url,
        map: this.map,
      });
    };
  })
})();

(() => {
  if (typeof window.customElements.get('location-pref-button') !== 'undefined') { return }
  window.customElements.define('location-pref-button', class LocationsPrefButton extends HTMLElement { // eslint-disable-line
    constructor() {
      super()
      this.button = this.querySelector('[data-prefer-this-location]')
      this.locationEl = this.closest('location-section')
      this.init()
    }

    init() {
      if (!this.button || !this.locationEl) { return }
      this.button.addEventListener('click', () => {
        this.locationEl.updatePreferredClinic(this.dataset.locationId)
      })
    }
  })
})();
